import { useEffect, useRef, useState } from "react";
import {
  deleteTable,
  deleteZone,
  nextTableId,
  nextTableName,
  notify,
  addTable as storeAddTable,
  addZone as storeAddZone,
  toggleNeighbor,
  updateTable,
  updateZone,
  useAppStore,
} from "@/store/store";
import type { TableShape as StoreShape, Table } from "@/store/types";
import { MapEngine } from "@/lib/map-engine";
import type { MapTable, MapZone, TableShape } from "@/lib/map-engine";
import {
  WORLD_BOUNDS,
  buildLayout,
  mapTableToStore,
  mapZoneToStore,
  storeTableToMap,
  tableEngineSize,
  worldToPct,
} from "@/lib/map-adapter";
import { brandZoneColor } from "@/lib/zone-brand-colors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import {
  MapToolbar,
  MapToolbarButton,
  MapToolbarDivider,
  MapToolbarModeToggle,
} from "@/components/map/MapToolbar";
import { loadCamera, saveCamera } from "@/lib/map-camera";
import { Link2, Pencil, Plus, Shrink, Trash2, Unlink2, Users } from "lucide-react";
import { useAuthStore } from "@/store/auth-store";
import { useCreateZone, useDeleteZone, useUpdateZone, useZones } from "@/hooks/use-zones";
import {
  useCreateTable,
  useDeleteTable,
  useTables,
  useToggleNeighbor,
  useUpdateTable,
} from "@/hooks/use-tables";
import { useLocations } from "@/hooks/use-locations";
import { findConnectedGroup } from "@/lib/table-graph";
import {
  useCreateTableGroup,
  useDeleteTableGroup,
  useTableGroups,
  useUpdateTableGroup,
} from "@/hooks/use-table-groups";

// ── Types ─────────────────────────────────────────────────────

type ContextMenuState =
  | { kind: "table"; table: MapTable; x: number; y: number }
  | { kind: "zone"; zone: MapZone; x: number; y: number };

interface EditPanelState {
  type: "table" | "zone";
  id: string;
  anchorX: number; // Canvas-relative
  anchorY: number;
}

const PANEL_W = 288; // matches Tailwind w-72
const PANEL_GAP = 20;

// ``findConnectedGroup`` was moved to ``@/lib/table-graph`` so MapCanvas, the
// walk-in panel and any future consumer share one traversal implementation.

// ── Component ─────────────────────────────────────────────────

export default function MapCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<MapEngine | null>(null);
  const shouldAutoFit = useRef(false);
  const [zoom, setZoom] = useState(85);
  const [ctx, setCtx] = useState<ContextMenuState | null>(null);
  const [edit, setEdit] = useState<EditPanelState | null>(null);
  // Group editor: shown when the user taps the "Grupo" button on a table that
  // has neighbors. ``tableIds`` is the connected component the halo represents.
  const [groupEdit, setGroupEdit] = useState<{
    tableIds: string[];
    panelPos: { left: number; top: number };
  } | null>(null);
  const [connectMode, setConnectMode] = useState(false);
  const [floatingDelete, setFloatingDelete] = useState<{
    id: string;
    kind: "table" | "zone";
    isBlock: boolean;
    x: number;
    y: number;
  } | null>(null);
  // Hide the floating delete button while the user is pressing on the canvas.
  // The engine cancels in-flight drags on `mouseleave`, so if the cursor
  // crosses from the canvas onto this absolutely-positioned button mid-drag,
  // the drop event never fires.
  const [isCanvasPressed, setIsCanvasPressed] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    kind: "table" | "zone";
    isBlock: boolean;
    id: string;
    label: string;
  } | null>(null);
  const [pendingDisconnect, setPendingDisconnect] = useState<{
    id: string;
    label: string;
    neighborNames: string[];
  } | null>(null);
  // Halo data + capacity totals rendered when a table belongs to a multi-
  // table connected group. The chip is positioned in JSX directly from the
  // floating-action pill anchor (not stored here), so it always sits
  // centered on the pill — regardless of which table in the group is
  // selected and where the group's bounding box is.
  const [connectionHalo, setConnectionHalo] = useState<{
    rootId: string;
    tables: {
      id: string;
      isRound: boolean;
      bounds: { left: number; top: number; width: number; height: number };
    }[];
    totalCapacity: number;
    breakdown: string;
  } | null>(null);
  const isEditMode = useAppStore((s) => s.mapMode === "edit");

  const storeZones = useAppStore((s) => s.zones);
  const storeTables = useAppStore((s) => s.tables);

  // Tables created before their zone is persisted — flushed on zone create success
  const pendingTableCreates = useRef<Map<string, Table>>(new Map());

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const isUUID = (id: string) => UUID_RE.test(id);

  // API mutations
  const { data: locations } = useLocations();
  const locationId = locations?.[0]?.id ?? "";
  // Editor and dashboard share the same sede's camera, so we scope both on
  // the active location from the auth store.
  const activeLocationId = useAuthStore((s) => s.locationId);
  const tablesQuery = useTables();
  const zonesQuery = useZones();
  const isLoadingMap = tablesQuery.isPending || zonesQuery.isPending;
  const createZoneMut = useCreateZone();
  const updateZoneMut = useUpdateZone();
  const deleteZoneMut = useDeleteZone();
  const createTableMut = useCreateTable();
  const updateTableMut = useUpdateTable();
  const deleteTableMut = useDeleteTable();
  const toggleNeighborMut = useToggleNeighbor();
  const tableGroupsQuery = useTableGroups();
  const createTableGroupMut = useCreateTableGroup();
  const updateTableGroupMut = useUpdateTableGroup();
  const deleteTableGroupMut = useDeleteTableGroup();

  // Reload engine when API data arrives (initial load)
  const engineLoaded = useRef(false);
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || storeTables.length === 0 || engineLoaded.current) return;
    engineLoaded.current = true;
    const cam = {
      offsetX: engine.camera.offsetX,
      offsetY: engine.camera.offsetY,
      scale: engine.camera.scale,
    };
    engine.load(buildLayout(storeTables, storeZones));
    if (shouldAutoFit.current) {
      shouldAutoFit.current = false;
      engine.fitToContent();
      if (activeLocationId) saveCamera(activeLocationId, engine.camera);
      setZoom(engine.camera.zoomPercent);
    } else {
      Object.assign(engine.camera, cam);
    }
    engine.requestRender();
  }, [storeTables, storeZones, activeLocationId]);

  // ── Engine lifecycle ─────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const cam = loadCamera(activeLocationId);
    shouldAutoFit.current = !cam;
    const engine = new MapEngine(canvas, {
      mode: "edit",
      worldBounds: WORLD_BOUNDS,
      ...(cam
        ? { initialOffsetX: cam.offsetX, initialOffsetY: cam.offsetY, initialScale: cam.scale }
        : {}),
    });

    // Load data from store
    const layout = buildLayout(storeTables, storeZones);
    engine.load(layout);

    // ── Event listeners ──────────────────────────────────────────

    // Debounced camera save — fires after pan/zoom settles
    let cameraSaveTimer: ReturnType<typeof setTimeout>;
    function scheduleCameraSave() {
      clearTimeout(cameraSaveTimer);
      cameraSaveTimer = setTimeout(() => {
        if (activeLocationId) saveCamera(activeLocationId, engine.camera);
      }, 300);
    }

    // Initial anchor for the floating delete button. The rAF loop below keeps
    // it in sync after this — these handlers just seed the value on selection.
    const anchorFor = (t: { x: number; y: number; w: number }) => {
      const s = engine.camera.worldToScreen(t.x + t.w, t.y);
      // Offset outside the bounding box so it doesn't cover the `ne` resize
      // handle nor fight the top-edge handles when the zone is small.
      return { x: s.x + 12, y: s.y - 28 };
    };

    // Compute per-table halo bounds + capacity totals for the connected
    // group containing ``rootId``. Returns null when the table is solo (no
    // neighbors) or has gone away. Reads the latest store state so freshly-
    // toggled connections show up immediately.
    const computeHalo = (rootId: string) => {
      const tables = useAppStore.getState().tables;
      const group = findConnectedGroup(rootId, tables);
      if (group.length < 2) return null;

      const padding = 8;
      const tableBounds: NonNullable<typeof connectionHalo>["tables"] = [];
      const capacities: number[] = [];
      for (const id of group) {
        const mt = engine.getTable(id);
        const t = tables.find((x) => x.id === id);
        if (!mt || !t) continue;
        const tl = engine.camera.worldToScreen(mt.x, mt.y);
        const br = engine.camera.worldToScreen(mt.x + mt.w, mt.y + mt.h);
        tableBounds.push({
          id,
          isRound: mt.shape === "circle",
          bounds: {
            left: tl.x - padding,
            top: tl.y - padding,
            width: br.x - tl.x + padding * 2,
            height: br.y - tl.y + padding * 2,
          },
        });
        capacities.push(t.maxCapacity);
      }
      if (tableBounds.length < 2) return null;

      return {
        rootId,
        tables: tableBounds,
        totalCapacity: capacities.reduce((s, c) => s + c, 0),
        breakdown: capacities.join(" + "),
      };
    };

    engine.on("zoom", (pct) => {
      setZoom(pct);
      scheduleCameraSave();
    });

    engine.on("table:select", (table) => {
      setCtx(null);
      if (table) {
        const a = anchorFor(table);
        setFloatingDelete({ id: table.id, kind: "table", isBlock: false, x: a.x, y: a.y });
        setConnectionHalo(computeHalo(table.id));
      } else {
        setFloatingDelete((prev) => (prev?.kind === "table" ? null : prev));
        setConnectionHalo(null);
      }
    });

    engine.on("zone:select", (zone) => {
      setCtx(null);
      if (zone) {
        const a = anchorFor(zone);
        setFloatingDelete({
          id: zone.id,
          kind: "zone",
          isBlock: !!zone.isBlock,
          x: a.x,
          y: a.y,
        });
      } else {
        setFloatingDelete((prev) => (prev?.kind === "zone" ? null : prev));
      }
      setConnectionHalo(null);
    });

    engine.on("table:contextmenu", ({ table, x, y }) => {
      // Disabled tables don't expose any edit menu — re-enable from admin.
      if (table.status === "unavailable") {
        return;
      }
      setCtx({ kind: "table", table, x, y });
      setEdit(null);
    });

    engine.on("zone:contextmenu", ({ zone, x, y }) => {
      // Policy change (2026-06-04): zones marked ``is_available=false``
      // can still be re-positioned / resized from the editor. The
      // availability flag only gates reservation flows, not layout
      // work.
      setCtx({ kind: "zone", zone, x, y });
      setEdit(null);
    });

    engine.on("table:dblclick", (table) => {
      const s = engine.camera.worldToScreen(table.x + table.w / 2, table.y + table.h / 2);
      setEdit({ anchorX: s.x, anchorY: s.y, id: table.id, type: "table" });
      setCtx(null);
    });

    engine.on("zone:dblclick", (zone) => {
      const s = engine.camera.worldToScreen(zone.x + zone.w / 2, zone.y + zone.h / 2);
      setEdit({ anchorX: s.x, anchorY: s.y, id: zone.id, type: "zone" });
      setCtx(null);
    });

    engine.on("table:drop", ({ table, fromZone, toZone }) => {
      const original = useAppStore.getState().tables.find((t) => t.id === table.id);
      if (original) {
        const changes = mapTableToStore(table, original);
        updateTable(table.id, changes);

        if (pendingTableCreates.current.has(table.id)) {
          // Table was created before its zone was saved — create it now if zone is real
          const realZone = toZone && isUUID(toZone) ? toZone : null;
          if (realZone) {
            const localId = table.id;
            createTableMut.mutate(
              { ...original, ...changes, zoneId: realZone },
              {
                onSuccess: (apiTable) => {
                  engineRef.current?.updateTable(localId, { id: apiTable.id });
                },
              },
            );
            pendingTableCreates.current.delete(table.id);
          }
        } else if (isUUID(table.id)) {
          // Strip non-UUID (and empty-string) zone references from the update
          // payload — backend expects a real UUID or omitted, not "".
          const apiChanges = { ...changes } as Record<string, unknown>;
          const candidate = apiChanges.zoneId;
          if (typeof candidate !== "string" || candidate === "" || !isUUID(candidate)) {
            delete apiChanges.zoneId;
          }
          // Snapshot pre-drag position + zone in engine coords. The hook's
          // onError refetches and re-syncs the store, but the engine has
          // its own copy of the table; without an explicit revert, the
          // canvas keeps showing the rejected position.
          const restoredZone = useAppStore.getState().zones.find((z) => z.id === original.zoneId);
          const restored = storeTableToMap(original, restoredZone ?? null);
          updateTableMut.mutate(
            { id: table.id, changes: apiChanges },
            {
              onError: () => {
                engineRef.current?.updateTable(table.id, {
                  x: restored.x,
                  y: restored.y,
                  zone: restored.zone,
                });
              },
            },
          );
        }
      }
      if (fromZone !== toZone) {
        const zoneName = toZone ? engine.getZone(toZone)?.label || toZone : "Sin zona";
        notify("info", "Mesa movida", `${table.label} → ${zoneName}`);
      }
    });

    engine.on("zone:resize", (zone) => {
      const changes = mapZoneToStore(zone);
      updateZone(zone.id, changes);
      if (isUUID(zone.id)) {
        updateZoneMut.mutate({ id: zone.id, changes });
        for (const mt of engine.getZoneTables(zone.id)) {
          const original = useAppStore.getState().tables.find((t) => t.id === mt.id);
          if (original) {
            const tableChanges = mapTableToStore(mt, original);
            updateTable(mt.id, tableChanges);
            if (isUUID(mt.id)) updateTableMut.mutate({ id: mt.id, changes: tableChanges });
          }
        }
      }
    });

    engine.on("connect:toggle", ({ idA, idB, connected }) => {
      // Engine already painted the line optimistically. Don't touch the
      // store yet — let the API answer. On 409 we flip the engine back so
      // the canvas matches the rejected state instead of leaving a zombie
      // line that only disappears on hard refresh.
      const tables = useAppStore.getState().tables;
      const tableA = tables.find((t) => t.id === idA);
      const tableB = tables.find((t) => t.id === idB);
      if (!tableA || !tableB) return;
      const tableNeighbors = connected
        ? [...tableA.neighbors.filter((id) => id !== idB), idB]
        : tableA.neighbors.filter((id) => id !== idB);
      const neighborNeighbors = connected
        ? [...tableB.neighbors.filter((id) => id !== idA), idA]
        : tableB.neighbors.filter((id) => id !== idA);
      toggleNeighborMut.mutate(
        { tableId: idA, neighborId: idB, tableNeighbors, neighborNeighbors },
        {
          onSuccess: () => {
            notify("info", connected ? "Mesas conectadas" : "Conexión eliminada", "");
          },
          onError: () => {
            // Revert the engine's optimistic state via ``setConnections`` — NOT
            // ``toggleConnection``, which re-emits ``connect:toggle`` and would
            // re-enter this handler with the inverse ``connected`` flag, firing
            // a phantom "Conexión eliminada" success toast.
            const current = engine.getConnections();
            const next = connected
              ? current.filter(([a, b]) => !((a === idA && b === idB) || (a === idB && b === idA)))
              : [...current, [idA, idB] as [string, string]];
            engine.setConnections(next);
            engine.requestRender();
          },
        },
      );
      setConnectMode(false);
    });

    // Intercept Delete key to also persist to API
    // Use capture phase so we read selectedTableId before the engine clears it
    const handleKeyDelete = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (!engine.isEditMode) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (engine.selectedTableId !== null) {
        const id = engine.selectedTableId;
        deleteTable(id);
        deleteTableMut.mutate(id);
        return;
      }

      // Zones (and blocks, which are zones with isBlock=true) — the engine
      // has no built-in keyboard delete for zones, so we drive it here.
      if (engine.selectedZoneId !== null) {
        const id = engine.selectedZoneId;
        engine.removeZone(id);
        deleteZone(id);
        if (isUUID(id)) deleteZoneMut.mutate(id);
        setEdit(null);
      }
    };
    window.addEventListener("keydown", handleKeyDelete, true);

    // Save camera after any pan/drag ends
    canvas.addEventListener("mouseup", scheduleCameraSave);
    canvas.addEventListener("touchend", scheduleCameraSave);

    // Track canvas press so the floating delete button can hide during drags
    // — see `isCanvasPressed` declaration for context. Also dismiss the edit
    // panel on any canvas interaction (tap-outside-to-close).
    const onPressDown = () => {
      setIsCanvasPressed(true);
      setEdit(null);
      setGroupEdit(null);
    };
    const onPressUp = () => setIsCanvasPressed(false);
    canvas.addEventListener("mousedown", onPressDown);
    canvas.addEventListener("touchstart", onPressDown);
    window.addEventListener("mouseup", onPressUp);
    window.addEventListener("touchend", onPressUp);
    window.addEventListener("touchcancel", onPressUp);

    engineRef.current = engine;
    setZoom(engine.camera.zoomPercent);
    return () => {
      clearTimeout(cameraSaveTimer);
      window.removeEventListener("keydown", handleKeyDelete, true);
      canvas.removeEventListener("mouseup", scheduleCameraSave);
      canvas.removeEventListener("touchend", scheduleCameraSave);
      canvas.removeEventListener("mousedown", onPressDown);
      canvas.removeEventListener("touchstart", onPressDown);
      window.removeEventListener("mouseup", onPressUp);
      window.removeEventListener("touchend", onPressUp);
      window.removeEventListener("touchcancel", onPressUp);
      // Flush the last camera position so the mode toggle never loses it,
      // even if the user pans and toggles within the 300ms debounce window.
      if (activeLocationId) saveCamera(activeLocationId, engine.camera);
      engine.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push the connected-group ids down into the engine so the renderer can
  // draw a soft glow behind those tables (visually beneath the table fill,
  // which is impossible from any HTML overlay since the canvas is opaque).
  // We gate on edit mode + no panel/context-menu/press so the glow only
  // shows when the floating actions and chip are visible.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    const visible = connectionHalo !== null && isEditMode && !edit && !ctx && !isCanvasPressed;
    const next = visible ? connectionHalo.tables.map((t) => t.id) : [];
    engine.glowingTableIds = next;
    engine.requestRender();
  }, [connectionHalo, isEditMode, edit, ctx, isCanvasPressed]);

  // Keep the floating delete button glued to its target while it's being
  // dragged or the camera moves. The engine doesn't emit per-frame events,
  // so we poll via rAF only while a selection exists.
  useEffect(() => {
    if (!floatingDelete) return;
    const engine = engineRef.current;
    if (!engine) return;

    let raf = 0;
    const tick = () => {
      setFloatingDelete((prev) => {
        if (!prev) return prev;
        const target = prev.kind === "table" ? engine.getTable(prev.id) : engine.getZone(prev.id);
        if (!target) return null;
        const s = engine.camera.worldToScreen(target.x + target.w, target.y);
        const nx = s.x + 12;
        const ny = s.y - 28;
        if (nx === prev.x && ny === prev.y) return prev;
        return { ...prev, x: nx, y: ny };
      });

      // Re-compute the per-table halo bounds + chip anchor from the engine's
      // current table positions. Done inline (instead of calling
      // ``computeHalo``) because that helper is scoped to the engine-setup
      // effect.
      setConnectionHalo((prev) => {
        if (!prev) return prev;
        const tables = useAppStore.getState().tables;
        const group = findConnectedGroup(prev.rootId, tables);
        if (group.length < 2) return null;

        const padding = 8;
        const tableBounds: typeof prev.tables = [];
        const capacities: number[] = [];
        for (const id of group) {
          const mt = engine.getTable(id);
          const t = tables.find((x) => x.id === id);
          if (!mt || !t) continue;
          const tl = engine.camera.worldToScreen(mt.x, mt.y);
          const br = engine.camera.worldToScreen(mt.x + mt.w, mt.y + mt.h);
          tableBounds.push({
            id,
            isRound: mt.shape === "circle",
            bounds: {
              left: tl.x - padding,
              top: tl.y - padding,
              width: br.x - tl.x + padding * 2,
              height: br.y - tl.y + padding * 2,
            },
          });
          capacities.push(t.maxCapacity);
        }
        if (tableBounds.length < 2) return null;

        const next = {
          rootId: prev.rootId,
          tables: tableBounds,
          totalCapacity: capacities.reduce((s, c) => s + c, 0),
          breakdown: capacities.join(" + "),
        };

        // Skip the React re-render when nothing actually moved.
        if (
          prev.tables.length === next.tables.length &&
          prev.totalCapacity === next.totalCapacity &&
          prev.breakdown === next.breakdown &&
          prev.tables.every((p, i) => {
            const n = next.tables[i];
            return (
              p.id === n.id &&
              p.isRound === n.isRound &&
              p.bounds.left === n.bounds.left &&
              p.bounds.top === n.bounds.top &&
              p.bounds.width === n.bounds.width &&
              p.bounds.height === n.bounds.height
            );
          })
        ) {
          return prev;
        }
        return next;
      });

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [floatingDelete?.id, floatingDelete?.kind]);

  // ── Actions ──────────────────────────────────────────────────

  const handleAddTable = (shape: TableShape) => {
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    // Backend requires zone_id (UUID). If the user hasn't created any zones
    // yet, creation would fail with 422 — block it early with a clear toast.
    const fallbackZoneId = storeZones.find((z) => !z.isBlock)?.id;
    if (!fallbackZoneId) {
      notify("warning", "Creá una zona primero", "");
      return;
    }
    const mt = engine.addTable(shape);
    const id = nextTableId();
    const name = nextTableName();
    mt.id = id;
    mt.label = name;
    // The engine seeds new tables at a fixed 70×70, but ``storeTableToMap``
    // derives w/h from capacity. Without aligning them here the table renders
    // at the wrong size until a remount reloads the engine from the store.
    const storeShape: StoreShape = shape === "circle" ? "round" : "square";
    const { w: engineW, h: engineH } = tableEngineSize(mt.capacity, storeShape);
    mt.w = engineW;
    mt.h = engineH;
    if (!mt.zone) {
      mt.zone = fallbackZoneId;
      // Viewport center landed outside any zone — drop the table at the
      // fallback zone's center so it doesn't end up orphaned on the canvas.
      const zone = engine.getZone(fallbackZoneId);
      if (zone) {
        mt.x = zone.x + zone.w / 2 - mt.w / 2;
        mt.y = zone.y + zone.h / 2 - mt.h / 2;
      }
      engine.requestRender();
    }
    const table = {
      // Engine MapTable carries only ``capacity`` (visual sizing). When we
      // promote a freshly-spawned table into the store we hydrate min from
      // the engine's value too — the store/API care about both.
      minCapacity: mt.capacity,
      maxCapacity: mt.capacity,
      id,
      name,
      neighbors: [] as string[],
      shape: storeShape,
      features: [] as string[],
      x: worldToPct(mt.x, "x"),
      y: worldToPct(mt.y, "y"),
      zoneId: mt.zone,
      isAvailable: true,
    };
    storeAddTable(table);
    if (isUUID(table.zoneId)) {
      // Swap the engine's local id with the server-side UUID once the API
      // call returns — otherwise the next ``table:drop`` for this freshly-
      // created table fires with the local id, which no longer exists in
      // the store (the store-sync useEffect replaces it with the API row),
      // and the drop handler bails out without saving the new position.
      createTableMut.mutate(table, {
        onSuccess: (apiTable) => {
          engineRef.current?.updateTable(id, { id: apiTable.id });
        },
      });
    } else {
      // Zone not yet persisted — queue creation until table is dropped into a real zone
      pendingTableCreates.current.set(id, table);
    }
    notify(
      "success",
      "Mesa creada",
      mt.zone ? `${name} creada en la zona ${engine.getZone(mt.zone)?.label}` : `${name} creada`,
    );
  };

  const handleAddZone = () => {
    const engine = engineRef.current;
    if (!engine || !locationId) {
      return;
    }
    const mz = engine.addZone();
    const zone = {
      borderColor: mz.border,
      color: mz.color,
      height: 20,
      id: mz.id,
      name: "Nueva Zona",
      width: 25,
      x: 40,
      y: 40,
      isAvailable: true,
    };
    storeAddZone(zone);
    createZoneMut.mutate(
      { zone },
      {
        onSuccess: (apiZone) => {
          const localId = zone.id;
          const realId = apiZone.id;
          // Update engine and any tables already placed in this zone
          engineRef.current?.updateZone(localId, { id: realId });
          engineRef.current?.tables
            .filter((t) => t.zone === localId)
            .forEach((t) => engineRef.current?.updateTable(t.id, { zone: realId }));
          // Flush tables waiting for this zone
          for (const [tId, tData] of pendingTableCreates.current) {
            if (tData.zoneId === localId) {
              const pendingLocalId = tId;
              createTableMut.mutate(
                { ...tData, zoneId: realId },
                {
                  onSuccess: (apiTable) => {
                    engineRef.current?.updateTable(pendingLocalId, { id: apiTable.id });
                  },
                },
              );
              pendingTableCreates.current.delete(tId);
            }
          }
        },
      },
    );
    notify("success", "Zona creada", "");
  };

  const handleAddBlock = () => {
    const engine = engineRef.current;
    if (!engine || !locationId) {
      return;
    }
    const mz = engine.addZone();
    mz.isBlock = true;
    mz.label = "BLOQUE";
    mz.color = "#e5e5e5";
    mz.border = "#a1a1aa";
    mz.labelColor = "#a1a1aa";
    engine.requestRender();
    const zone = {
      borderColor: "#a1a1aa",
      color: "#e5e5e5",
      height: 10,
      id: mz.id,
      isBlock: true,
      name: "Bloque",
      width: 15,
      x: 40,
      y: 40,
      isAvailable: true,
    };
    storeAddZone(zone);
    createZoneMut.mutate(
      { zone },
      {
        onSuccess: (apiZone) => {
          const localId = zone.id;
          const realId = apiZone.id;
          engineRef.current?.updateZone(localId, { id: realId });
        },
      },
    );
    notify("success", "Bloque creado", "");
  };

  const handleToggleConnect = () => {
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    const next = !connectMode;
    engine.setConnectMode(next);
    setConnectMode(next);
  };

  const handleReset = () => {
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    engine.fitToContent();
    setZoom(engine.camera.zoomPercent);
    if (activeLocationId) saveCamera(activeLocationId, engine.camera);
  };

  const handleDeleteTable = (id: string) => {
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    engine.removeTable(id);
    deleteTable(id);
    deleteTableMut.mutate(id);
    setCtx(null);
    setEdit(null);
    notify("info", "Mesa eliminada", "");
  };

  const handleDisconnect = (id: string) => {
    engineRef.current?.disconnectTable(id);
    const original = useAppStore.getState().tables.find((t) => t.id === id);
    if (original) {
      for (const nId of original.neighbors) {
        toggleNeighbor(id, nId);
        const tables = useAppStore.getState().tables;
        toggleNeighborMut.mutate({
          tableId: id,
          neighborId: nId,
          tableNeighbors: tables.find((t) => t.id === id)?.neighbors ?? [],
          neighborNeighbors: tables.find((t) => t.id === nId)?.neighbors ?? [],
        });
      }
    }
    setCtx(null);
    notify("info", "Conexiones eliminadas", "");
  };

  // ── Edit panel save handlers ──────────────────────────────────

  const handleDeleteZone = (id: string) => {
    engineRef.current?.removeZone(id);
    deleteZone(id);
    if (isUUID(id)) deleteZoneMut.mutate(id);
    setEdit(null);
    notify("info", "Zona eliminada", "");
  };

  // ── Render ───────────────────────────────────────────────────

  const engine = engineRef.current;
  const selectedTable = edit?.type === "table" ? engine?.getTable(edit.id as string) : null;
  const selectedZone = edit?.type === "zone" ? engine?.getZone(edit.id as string) : null;

  // Compute panel position anchored to the object
  let panelPos: { left: number; top: number } | null = null;
  if (edit) {
    const cw = canvasRef.current?.clientWidth || 900;
    const ch = canvasRef.current?.clientHeight || 600;
    // Reserve space for the panel itself so it never hangs off the bottom of
    // the viewport (which on a tablet would slide under the on-screen keyboard).
    const panelReserve = 460;
    let left = edit.anchorX + PANEL_GAP;
    if (left + PANEL_W > cw - 12) {
      left = edit.anchorX - PANEL_W - PANEL_GAP;
    }
    left = Math.max(12, Math.min(left, cw - PANEL_W - 12));
    let top = edit.anchorY - 40;
    top = Math.max(12, Math.min(top, Math.max(12, ch - panelReserve)));
    panelPos = { left, top };
  }

  return (
    <div
      className="relative w-full h-full flex-1 overflow-hidden"
      style={{ background: "#f2f2f0" }}
    >
      <canvas
        ref={canvasRef}
        style={{ cursor: "grab", display: "block", height: "100%", width: "100%" }}
      />

      {isLoadingMap && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#f2f2f0]/70 backdrop-blur-sm pointer-events-none">
          <Spinner className="size-8 text-text-secondary" />
        </div>
      )}

      {/* ── Toolbar ───────────────────────────────────────────── */}
      <MapToolbar>
        <MapToolbarButton onClick={handleReset} label="Ajustar al mapa">
          <Shrink className="size-4" />
        </MapToolbarButton>
        <span className="text-[11px] font-mono text-gray-400 tabular-nums min-w-9 text-center px-1">
          {zoom}%
        </span>

        <MapToolbarDivider />

        <MapToolbarButton onClick={() => handleAddTable("square")} label="Agregar mesa">
          <AddIconBadge>
            <ToolbarIcon type="table" />
          </AddIconBadge>
        </MapToolbarButton>
        <MapToolbarButton onClick={handleAddZone} label="Agregar zona">
          <AddIconBadge>
            <ToolbarIcon type="zone" />
          </AddIconBadge>
        </MapToolbarButton>
        <MapToolbarButton onClick={handleAddBlock} label="Agregar bloque">
          <AddIconBadge>
            <ToolbarIcon type="block" />
          </AddIconBadge>
        </MapToolbarButton>
        <MapToolbarButton
          onClick={handleToggleConnect}
          label="Agregar conexión"
          active={connectMode}
        >
          <AddIconBadge>
            <ToolbarIcon type="connect" />
          </AddIconBadge>
        </MapToolbarButton>

        <MapToolbarDivider />

        <MapToolbarModeToggle />
      </MapToolbar>

      {/* ── Group chips for the tapped table ───────────────────── */}
      {/* Anchored to the floating-action pill so it follows it 1:1. The pill
          is centered on (floatingDelete.x, floatingDelete.y) and is roughly
          48 px tall (size-9 buttons + p-1.5 padding); the bottom-most chip
          sits 8 px above its top edge and the stack grows upward. Each chip
          is one ``TableGroup`` containing the tapped mesa. */}
      {floatingDelete &&
        floatingDelete.kind === "table" &&
        isEditMode &&
        !edit &&
        !groupEdit &&
        !ctx &&
        !isCanvasPressed &&
        (() => {
          const mesaGroups =
            tableGroupsQuery.data?.filter((g) => g.tables.includes(floatingDelete.id)) ?? [];
          if (mesaGroups.length === 0) return null;
          return (
            <div
              className="absolute z-[9] pointer-events-none flex -translate-x-1/2 -translate-y-full flex-col-reverse items-center gap-1"
              style={{
                left: floatingDelete.x,
                top: floatingDelete.y - 24 - 8,
              }}
            >
              {mesaGroups.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold tabular-nums text-white shadow-md animate-pop-in"
                >
                  <Users className="size-3" />
                  <span className="truncate max-w-[160px]">{g.name}</span>
                  <span className="opacity-90">·</span>
                  <span>
                    {g.min_capacity}–{g.max_capacity} personas
                  </span>
                </div>
              ))}
            </div>
          );
        })()}

      {/* ── Floating actions (tablet-friendly tap targets) ────── */}
      {floatingDelete &&
        isEditMode &&
        !edit &&
        !groupEdit &&
        !ctx &&
        !isCanvasPressed &&
        (() => {
          const isTable = floatingDelete.kind === "table";
          // Policy change (2026-06-04): we no longer suppress the
          // floating delete affordance for ``is_available=false``
          // tables or zones. The operator can still move them around
          // the canvas, and that includes removing them when they're
          // disabled.
          const tableNeighbors = isTable
            ? (storeTables.find((t) => t.id === floatingDelete.id)?.neighbors ?? [])
            : [];
          const hasNeighbors = tableNeighbors.length > 0;
          return (
            <div
              className="absolute z-[28] flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-full bg-white/85 p-1.5 shadow-lg ring-1 ring-black/5 backdrop-blur-md animate-pop-in"
              style={{ left: floatingDelete.x, top: floatingDelete.y }}
            >
              <button
                type="button"
                aria-label={
                  isTable ? "Editar mesa" : floatingDelete.isBlock ? "Editar bloque" : "Editar zona"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  const engine = engineRef.current;
                  if (!engine) return;
                  const target = isTable
                    ? engine.getTable(floatingDelete.id)
                    : engine.getZone(floatingDelete.id);
                  if (!target) return;
                  const s = engine.camera.worldToScreen(
                    target.x + target.w / 2,
                    target.y + target.h / 2,
                  );
                  setEdit({
                    anchorX: s.x,
                    anchorY: s.y,
                    id: floatingDelete.id,
                    type: floatingDelete.kind,
                  });
                }}
                className="flex size-9 items-center justify-center rounded-full text-foreground transition hover:bg-black/5 active:scale-95"
              >
                <Pencil className="size-[18px]" />
              </button>

              {isTable && (
                <>
                  <span className="h-5 w-px bg-black/10" />
                  <button
                    type="button"
                    aria-label={connectMode ? "Cancelar conexión" : "Conectar con otra mesa"}
                    title={connectMode ? "Cancelar conexión" : "Conectar"}
                    onClick={(e) => {
                      e.stopPropagation();
                      const engine = engineRef.current;
                      if (!engine) return;
                      if (connectMode) {
                        // Already in connect mode — tapping again cancels.
                        engine.setConnectMode(false);
                        engine.connectFirstId = null;
                        engine.requestRender();
                        setConnectMode(false);
                        return;
                      }
                      engine.setConnectMode(true);
                      engine.connectFirstId = floatingDelete.id;
                      engine.requestRender();
                      setConnectMode(true);
                    }}
                    className={`flex size-9 items-center justify-center rounded-full transition active:scale-95 ${
                      connectMode
                        ? "bg-emerald-500 text-white hover:bg-emerald-600"
                        : "text-foreground hover:bg-black/5"
                    }`}
                  >
                    <Link2 className="size-[18px]" />
                  </button>
                </>
              )}

              {isTable && hasNeighbors && (
                <>
                  <span className="h-5 w-px bg-black/10" />
                  <button
                    type="button"
                    aria-label="Quitar conexiones"
                    title="Desconectar"
                    onClick={(e) => {
                      e.stopPropagation();
                      const engine = engineRef.current;
                      const target = engine?.getTable(floatingDelete.id);
                      const neighborNames = tableNeighbors.map(
                        (nId) =>
                          storeTables.find((t) => t.id === nId)?.name ??
                          engine?.getTable(nId)?.label ??
                          "Mesa",
                      );
                      setPendingDisconnect({
                        id: floatingDelete.id,
                        label: target?.label ?? "",
                        neighborNames,
                      });
                    }}
                    className="flex size-9 items-center justify-center rounded-full text-foreground transition hover:bg-black/5 active:scale-95"
                  >
                    <Unlink2 className="size-[18px]" />
                  </button>
                  <span className="h-5 w-px bg-black/10" />
                  <button
                    type="button"
                    aria-label="Editar grupo"
                    title="Grupo"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Halo's connected component is the natural set for a
                      // group: every table reachable through the neighbour
                      // graph. Falls back to single id (degenerate) if for
                      // some reason the halo hasn't computed yet.
                      const ids = connectionHalo?.tables.map((t) => t.id) ?? [floatingDelete.id];
                      setGroupEdit({
                        tableIds: ids,
                        panelPos: { left: floatingDelete.x, top: floatingDelete.y + 32 },
                      });
                    }}
                    className="flex size-9 items-center justify-center rounded-full text-foreground transition hover:bg-black/5 active:scale-95"
                  >
                    <Users className="size-[18px]" />
                  </button>
                </>
              )}

              <span className="h-5 w-px bg-black/10" />
              <button
                type="button"
                aria-label={
                  isTable
                    ? "Eliminar mesa"
                    : floatingDelete.isBlock
                      ? "Eliminar bloque"
                      : "Eliminar zona"
                }
                onClick={(e) => {
                  e.stopPropagation();
                  const engine = engineRef.current;
                  const target = isTable
                    ? engine?.getTable(floatingDelete.id)
                    : engine?.getZone(floatingDelete.id);
                  setPendingDelete({
                    kind: floatingDelete.kind,
                    isBlock: floatingDelete.isBlock,
                    id: floatingDelete.id,
                    label: target?.label ?? "",
                  });
                }}
                className="flex size-9 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 active:scale-95"
              >
                <Trash2 className="size-[18px]" />
              </button>
            </div>
          );
        })()}

      {/* ── Context menu ──────────────────────────────────────── */}
      {ctx && (
        <>
          <div
            className="fixed inset-0 z-50"
            onClick={() => setCtx(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setCtx(null);
            }}
          />
          <div
            className="fixed z-[100] bg-white rounded-xl p-1.5 min-w-[210px] text-[13px] animate-pop-in"
            style={{ boxShadow: "0 8px 30px rgba(0,0,0,0.15)", left: ctx.x, top: ctx.y }}
          >
            {ctx.kind === "table" ? (
              <>
                <div className="px-3 py-1.5 text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                  Mesa {ctx.table.label}
                </div>
                <div
                  className="px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-2 hover:bg-gray-100"
                  onClick={() => {
                    const rect = canvasRef.current?.getBoundingClientRect();
                    setEdit({
                      anchorX: ctx.x - (rect?.left || 0),
                      anchorY: ctx.y - (rect?.top || 0),
                      id: ctx.table.id,
                      type: "table",
                    });
                    setCtx(null);
                  }}
                >
                  Editar
                </div>
                <div
                  className="px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-2 hover:bg-gray-100"
                  onClick={() => {
                    handleToggleConnect();
                    if (engine) {
                      engine.connectFirstId = ctx.table.id;
                      engine.requestRender();
                    }
                    setCtx(null);
                  }}
                >
                  Conectar
                </div>
                <div
                  className="px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-2 hover:bg-gray-100"
                  onClick={() => handleDisconnect(ctx.table.id)}
                >
                  Desconectar
                </div>
                <div className="h-px bg-gray-200 my-1 mx-1.5" />
                <div
                  className="px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-2 text-red-500 hover:bg-red-50"
                  onClick={() => handleDeleteTable(ctx.table.id)}
                >
                  Eliminar
                </div>
              </>
            ) : (
              <>
                <div className="px-3 py-1.5 text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                  {ctx.zone.isBlock ? "Bloque" : "Zona"} {ctx.zone.label}
                </div>
                <div
                  className="px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-2 hover:bg-gray-100"
                  onClick={() => {
                    const rect = canvasRef.current?.getBoundingClientRect();
                    setEdit({
                      anchorX: ctx.x - (rect?.left || 0),
                      anchorY: ctx.y - (rect?.top || 0),
                      id: ctx.zone.id,
                      type: "zone",
                    });
                    setCtx(null);
                  }}
                >
                  Editar
                </div>
                <div className="h-px bg-gray-200 my-1 mx-1.5" />
                <div
                  className="px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-2 text-red-500 hover:bg-red-50"
                  onClick={() => {
                    handleDeleteZone(ctx.zone.id);
                    setCtx(null);
                  }}
                >
                  {ctx.zone.isBlock ? "Eliminar bloque" : "Eliminar zona"}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ── Edit panel: Table ─────────────────────────────────── */}
      {selectedTable && panelPos && (
        <TableEditPanel
          key={selectedTable.id}
          table={selectedTable}
          panelPos={panelPos}
          onClose={() => setEdit(null)}
          onSave={(changes) => {
            const engine = engineRef.current;
            if (!engine) return;
            const enginePatch: Partial<MapTable> = {};
            const storeChanges: Record<string, unknown> = {};
            // Snapshot the engine's previous values so we can revert if the
            // backend rejects the patch (e.g. 409 on duplicate name).
            const enginePrev: Partial<MapTable> = {};
            if (changes.label !== undefined) {
              enginePrev.label = selectedTable.label;
              enginePatch.label = changes.label;
              storeChanges.name = changes.label;
            }
            if (changes.maxCapacity !== undefined) {
              // Engine's ``capacity`` only tracks the *visual* max — drives
              // the rendered table size. Store carries both min + max.
              enginePrev.capacity = selectedTable.capacity;
              enginePatch.capacity = changes.maxCapacity;
              storeChanges.maxCapacity = changes.maxCapacity;
            }
            if (changes.minCapacity !== undefined) {
              storeChanges.minCapacity = changes.minCapacity;
            }
            if (changes.shape !== undefined) {
              enginePrev.shape = selectedTable.shape;
              enginePatch.shape = changes.shape;
              storeChanges.shape = changes.shape === "circle" ? "round" : "square";
            }
            engine.updateTable(selectedTable.id, enginePatch);
            if (Object.keys(storeChanges).length > 0) {
              updateTable(selectedTable.id, storeChanges);
              if (isUUID(selectedTable.id)) {
                updateTableMut.mutate(
                  { id: selectedTable.id, changes: storeChanges },
                  {
                    onError: () => {
                      // Roll the engine back; the store-level onError already
                      // refetches and re-syncs ``storeTables`` for us.
                      engineRef.current?.updateTable(selectedTable.id, enginePrev);
                    },
                  },
                );
              }
            }
            setEdit(null);
          }}
          onDelete={() => handleDeleteTable(selectedTable.id)}
        />
      )}

      {/* ── Edit panel: Table group ───────────────────────────── */}
      {groupEdit && (
        <TableGroupPanel
          key={groupEdit.tableIds.join("-")}
          tableIds={groupEdit.tableIds}
          panelPos={groupEdit.panelPos}
          tables={storeTables}
          existingGroups={
            tableGroupsQuery.data?.filter(
              (g) => g.tables.length > 0 && g.tables.every((id) => groupEdit.tableIds.includes(id)),
            ) ?? []
          }
          onClose={() => setGroupEdit(null)}
          onSave={({ name, tables, minCapacity, maxCapacity }, existingId) => {
            const payload = {
              name,
              tables,
              min_capacity: minCapacity,
              max_capacity: maxCapacity,
            };
            if (existingId) {
              updateTableGroupMut.mutate(
                { id: existingId, changes: payload },
                { onSuccess: () => setGroupEdit(null) },
              );
            } else {
              createTableGroupMut.mutate(payload, {
                onSuccess: () => setGroupEdit(null),
              });
            }
          }}
          onDelete={(existingId) => {
            deleteTableGroupMut.mutate(existingId, {
              onSuccess: () => setGroupEdit(null),
            });
          }}
          isSaving={createTableGroupMut.isPending || updateTableGroupMut.isPending}
          isDeleting={deleteTableGroupMut.isPending}
        />
      )}

      {/* ── Edit panel: Zone ──────────────────────────────────── */}
      {selectedZone && panelPos && (
        <ZoneEditPanel
          key={selectedZone.id}
          zone={selectedZone}
          panelPos={panelPos}
          onClose={() => setEdit(null)}
          onSave={(changes) => {
            const engine = engineRef.current;
            if (!engine) return;
            const enginePatch: Partial<MapZone> = {};
            const storeChanges: Record<string, unknown> = {};
            const enginePrev: Partial<MapZone> = {};
            // If the rename matches one of our brand zones, swap the
            // engine's palette to the brand recipe and DON'T forward
            // the new color/border to the backend — preMixedColors=true
            // tells the renderer (and mapZoneToStore) that the visible
            // colours are an overlay, not the operator's choice.
            const nextLabel = changes.label !== undefined ? changes.label : selectedZone.label;
            const nextBrand = selectedZone.isBlock ? null : brandZoneColor(nextLabel);
            if (changes.label !== undefined) {
              enginePrev.label = selectedZone.label;
              enginePatch.label = changes.label.toUpperCase();
              storeChanges.name = changes.label;
            }
            if (nextBrand) {
              enginePrev.color = selectedZone.color;
              enginePrev.border = selectedZone.border;
              enginePrev.labelColor = selectedZone.labelColor;
              enginePrev.preMixedColors = selectedZone.preMixedColors;
              enginePatch.color = nextBrand.fill;
              enginePatch.border = nextBrand.border;
              enginePatch.labelColor = nextBrand.label;
              enginePatch.preMixedColors = true;
            } else {
              if (changes.color !== undefined) {
                enginePrev.color = selectedZone.color;
                enginePatch.color = changes.color;
                storeChanges.color = changes.color;
              }
              if (changes.border !== undefined) {
                enginePrev.border = selectedZone.border;
                enginePrev.labelColor = selectedZone.labelColor;
                enginePatch.border = changes.border;
                enginePatch.labelColor = changes.border;
                storeChanges.borderColor = changes.border;
              }
              // Coming back from a brand name to a non-brand one:
              // restore legacy attenuation so the picked colour
              // renders with the same recipe as before the rebrand.
              if (selectedZone.preMixedColors === true) {
                enginePrev.preMixedColors = true;
                enginePatch.preMixedColors = false;
              }
            }
            engine.updateZone(selectedZone.id, enginePatch);
            if (Object.keys(storeChanges).length > 0) {
              updateZone(selectedZone.id, storeChanges);
              if (isUUID(selectedZone.id)) {
                updateZoneMut.mutate(
                  { id: selectedZone.id, changes: storeChanges },
                  {
                    onError: () => {
                      engineRef.current?.updateZone(selectedZone.id, enginePrev);
                    },
                  },
                );
              }
            }
            setEdit(null);
          }}
          onDelete={() => handleDeleteZone(selectedZone.id)}
        />
      )}

      <style>{`
        .animate-pop-in { animation: popIn 0.1s ease }
        .animate-slide-in { animation: slideIn 0.12s ease }
        @keyframes popIn { from { opacity:0;transform:scale(0.96) } to { opacity:1;transform:scale(1) } }
        @keyframes slideIn { from { opacity:0;transform:translateX(8px) } to { opacity:1;transform:translateX(0) } }
      `}</style>

      <AlertDialog
        open={pendingDisconnect !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDisconnect(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Quitar las conexiones de {pendingDisconnect?.label}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDisconnect && pendingDisconnect.neighborNames.length > 0
                ? `Se desconectará de ${pendingDisconnect.neighborNames.length} ${
                    pendingDisconnect.neighborNames.length === 1 ? "mesa" : "mesas"
                  }: ${pendingDisconnect.neighborNames.join(", ")}.`
                : "Esta acción quitará todas las conexiones de esta mesa."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (pendingDisconnect) handleDisconnect(pendingDisconnect.id);
                setPendingDisconnect(null);
              }}
            >
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.kind === "table"
                ? `¿Eliminar la mesa ${pendingDelete?.label}?`
                : pendingDelete?.isBlock
                  ? `¿Eliminar el bloque ${pendingDelete?.label}?`
                  : `¿Eliminar la zona ${pendingDelete?.label}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.kind === "table"
                ? "Esta acción no se puede deshacer. Las reservas asignadas a esta mesa quedarán sin mesa."
                : "Esta acción no se puede deshacer. Las mesas en esta zona quedarán sin zona asignada."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (!pendingDelete) return;
                if (pendingDelete.kind === "table") {
                  handleDeleteTable(pendingDelete.id);
                } else {
                  handleDeleteZone(pendingDelete.id);
                }
                setPendingDelete(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────

function ToolbarIcon({ type }: { type: string }) {
  const s = { height: 16, viewBox: "0 0 16 16", width: 16 };
  switch (type) {
    case "table": {
      // Square table top with a chair on each side — distinct from a plain
      // rectangle (which is the zone/block icon).
      return (
        <svg {...s}>
          <rect
            x="4"
            y="4"
            width="8"
            height="8"
            rx="1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <line x1="2" y1="6.5" x2="2" y2="9.5" stroke="currentColor" strokeWidth="1.5" />
          <line x1="14" y1="6.5" x2="14" y2="9.5" stroke="currentColor" strokeWidth="1.5" />
          <line x1="6.5" y1="2" x2="9.5" y2="2" stroke="currentColor" strokeWidth="1.5" />
          <line x1="6.5" y1="14" x2="9.5" y2="14" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    }
    case "zone": {
      return (
        <svg {...s}>
          <rect
            x="1"
            y="1"
            width="14"
            height="14"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="3 2"
          />
        </svg>
      );
    }
    case "block": {
      return (
        <svg {...s}>
          <rect
            x="1"
            y="1"
            width="14"
            height="14"
            rx="3"
            fill="currentColor"
            opacity="0.2"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      );
    }
    case "connect": {
      return (
        <svg {...s}>
          <line
            x1="3"
            y1="13"
            x2="13"
            y2="3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeDasharray="3 2"
          />
          <circle cx="3" cy="13" r="2" fill="currentColor" />
          <circle cx="13" cy="3" r="2" fill="currentColor" />
        </svg>
      );
    }
    default: {
      return null;
    }
  }
}

function AddIconBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="relative inline-flex items-center justify-center">
      {children}
      <span className="pointer-events-none absolute -bottom-2 -right-2 flex size-2.5 items-center justify-center rounded-full bg-foreground text-background ring-1 ring-white">
        <Plus className="size-2" strokeWidth={3.5} />
      </span>
    </span>
  );
}

interface TableEditPanelProps {
  table: MapTable;
  panelPos: { left: number; top: number };
  onClose: () => void;
  onSave: (changes: {
    label?: string;
    minCapacity?: number;
    maxCapacity?: number;
    shape?: TableShape;
  }) => void;
  onDelete: () => void;
}

function TableEditPanel({ table, panelPos, onClose, onSave, onDelete }: TableEditPanelProps) {
  // The engine's ``MapTable`` only carries max (drives sizing). Min lives in
  // the store and the API.
  const storeTable = useAppStore((s) => s.tables.find((t) => t.id === table.id));
  const initialMin = storeTable?.minCapacity ?? 1;
  const initialMax = storeTable?.maxCapacity ?? table.capacity;

  const [label, setLabel] = useState(table.label);
  const [minCapacity, setMinCapacity] = useState<number | "">(initialMin);
  const [maxCapacity, setMaxCapacity] = useState<number | "">(initialMax);
  const [shape, setShape] = useState<TableShape>(table.shape);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const minNum = typeof minCapacity === "number" ? minCapacity : null;
  const maxNum = typeof maxCapacity === "number" ? maxCapacity : null;
  const trimmedLabel = label.trim();
  const isDirty =
    trimmedLabel !== table.label ||
    (minNum !== null && minNum !== initialMin) ||
    (maxNum !== null && maxNum !== initialMax) ||
    shape !== table.shape;
  const capacitiesValid = minNum !== null && maxNum !== null && minNum >= 1 && maxNum >= minNum;
  const canSave = isDirty && trimmedLabel.length > 0 && capacitiesValid;

  const shapeLabel = (s: TableShape) => (s === "circle" ? "Redonda" : "Cuadrada");
  const changeRows: { key: string; label: string; from: string; to: string }[] = [];
  if (trimmedLabel !== table.label) {
    changeRows.push({ key: "label", label: "Nombre", from: table.label, to: trimmedLabel });
  }
  if (minNum !== null && minNum !== initialMin) {
    changeRows.push({
      key: "min",
      label: "Mínimo",
      from: String(initialMin),
      to: String(minNum),
    });
  }
  if (maxNum !== null && maxNum !== initialMax) {
    changeRows.push({
      key: "max",
      label: "Máximo",
      from: String(initialMax),
      to: String(maxNum),
    });
  }
  if (shape !== table.shape) {
    changeRows.push({
      key: "shape",
      label: "Forma",
      from: shapeLabel(table.shape),
      to: shapeLabel(shape),
    });
  }

  const handleConfirmSave = () => {
    if (!canSave || minNum === null || maxNum === null) return;
    const changes: {
      label?: string;
      minCapacity?: number;
      maxCapacity?: number;
      shape?: TableShape;
    } = {};
    if (trimmedLabel !== table.label) changes.label = trimmedLabel;
    if (minNum !== initialMin) changes.minCapacity = minNum;
    if (maxNum !== initialMax) changes.maxCapacity = maxNum;
    if (shape !== table.shape) changes.shape = shape;
    setSaveConfirmOpen(false);
    onSave(changes);
  };

  return (
    <div
      className="absolute z-[25] bg-white p-4 rounded-2xl w-72 text-sm animate-slide-in overflow-y-auto"
      style={{
        boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
        left: panelPos.left,
        maxHeight: "calc(100dvh - 80px)",
        top: panelPos.top,
      }}
    >
      <div className="flex justify-between items-center mb-4 gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground [&>svg]:size-6">
            <ToolbarIcon type="table" />
          </span>
          <h3 className="text-lg font-semibold text-gray-900 truncate">{label || table.label}</h3>
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          onClick={onClose}
        >
          <span className="text-2xl leading-none">&times;</span>
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
            Nombre
          </Label>
          <Input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="h-11 text-base px-3.5"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Mínimo
            </Label>
            <Input
              type="number"
              inputMode="numeric"
              value={minCapacity}
              min={1}
              max={20}
              onChange={(e) => {
                const raw = e.target.value;
                setMinCapacity(raw === "" ? "" : Number(raw));
              }}
              className="h-11 text-base px-2 text-center tabular-nums"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Máximo
            </Label>
            <Input
              type="number"
              inputMode="numeric"
              value={maxCapacity}
              min={1}
              max={20}
              onChange={(e) => {
                const raw = e.target.value;
                setMaxCapacity(raw === "" ? "" : Number(raw));
              }}
              className="h-11 text-base px-2 text-center tabular-nums"
            />
          </div>
        </div>

        {minNum !== null && maxNum !== null && minNum > maxNum && (
          <p className="text-xs text-red-600">El mínimo no puede ser mayor que el máximo.</p>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setShape("square")}
            className={`group flex items-center justify-center gap-2 h-11 rounded-lg border text-sm transition ${
              shape === "square"
                ? "border-foreground bg-muted/60 shadow-inner"
                : "border-input bg-background hover:bg-muted/40"
            }`}
          >
            <span
              className={`block size-4 rounded-sm border-2 transition ${
                shape === "square"
                  ? "border-foreground bg-foreground/10"
                  : "border-muted-foreground/60 group-hover:border-foreground/80"
              }`}
            />
            Cuadrada
          </button>
          <button
            type="button"
            onClick={() => setShape("circle")}
            className={`group flex items-center justify-center gap-2 h-11 rounded-lg border text-sm transition ${
              shape === "circle"
                ? "border-foreground bg-muted/60 shadow-inner"
                : "border-input bg-background hover:bg-muted/40"
            }`}
          >
            <span
              className={`block size-4 rounded-full border-2 transition ${
                shape === "circle"
                  ? "border-foreground bg-foreground/10"
                  : "border-muted-foreground/60 group-hover:border-foreground/80"
              }`}
            />
            Redonda
          </button>
        </div>
      </div>

      <div className="mt-5 flex items-stretch gap-2">
        <Button
          className="flex-1 h-11 text-base"
          disabled={!canSave}
          onClick={() => setSaveConfirmOpen(true)}
        >
          Guardar
        </Button>
        <Button
          variant="destructive"
          aria-label="Eliminar"
          title="Eliminar"
          className="size-11 shrink-0 p-0"
          onClick={() => setDeleteConfirmOpen(true)}
        >
          <Trash2 className="size-5" />
        </Button>
      </div>

      <AlertDialog open={saveConfirmOpen} onOpenChange={setSaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cambios</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a actualizar la mesa <span className="font-medium">{table.label}</span>. Revisá
              los cambios antes de continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ul className="mt-2 flex flex-col gap-2 text-sm">
            {changeRows.map((c) => (
              <li key={c.key} className="rounded-md border bg-muted/40 px-3 py-2">
                <div className="font-medium">{c.label}</div>
                <div className="text-muted-foreground">
                  <span className="line-through">{c.from}</span>
                  <span className="mx-2">→</span>
                  <span className="text-foreground">{c.to}</span>
                </div>
              </li>
            ))}
          </ul>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la mesa {table.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Las reservas asignadas a esta mesa quedarán sin
              mesa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setDeleteConfirmOpen(false);
                onDelete();
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Table group panel ─────────────────────────────────────────

interface TableGroupPanelProps {
  tableIds: string[];
  panelPos: { left: number; top: number };
  tables: Table[];
  existingGroups: import("@/types/api").ApiTableGroup[];
  onClose: () => void;
  onSave: (
    changes: { name: string; tables: string[]; minCapacity: number; maxCapacity: number },
    existingId: string | null,
  ) => void;
  onDelete: (existingId: string) => void;
  isSaving: boolean;
  isDeleting: boolean;
}

const MIN_GROUP_TABLES = 2;

function TableGroupPanel({
  tableIds,
  panelPos,
  tables,
  existingGroups,
  onClose,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
}: TableGroupPanelProps) {
  // The connected component is the *candidate set*; the user picks the subset
  // that should belong to this group.
  const candidateTables = tableIds
    .map((id) => tables.find((t) => t.id === id))
    .filter((t): t is Table => t != null);
  const defaultAutoName = candidateTables.map((t) => t.name).join(" + ");
  const defaultSumMin = candidateTables.reduce(
    (s, t) => s + (Number.isFinite(t.minCapacity) ? t.minCapacity : 1),
    0,
  );
  const defaultSumMax = candidateTables.reduce(
    (s, t) => s + (Number.isFinite(t.maxCapacity) ? t.maxCapacity : 0),
    0,
  );

  // ``undefined`` means the user hasn't chosen yet — picker is shown but the
  // form is hidden. ``null`` is the explicit "Nuevo grupo" mode, and a string
  // is the id of an existing group being edited.
  const [activeGroupId, setActiveGroupId] = useState<string | null | undefined>(undefined);
  const activeGroup =
    activeGroupId == null ? null : (existingGroups.find((g) => g.id === activeGroupId) ?? null);
  const formVisible = activeGroupId !== undefined;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [name, setName] = useState("");
  // ``true`` whenever the current name is an explicit choice (typed by the
  // user, or inherited from an existing group). Auto-name only kicks in for
  // brand-new groups whose name still mirrors the selection.
  const [nameTouched, setNameTouched] = useState(false);
  const [minCapacity, setMinCapacity] = useState<number | "">("");
  const [maxCapacity, setMaxCapacity] = useState<number | "">("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleSelectGroup = (groupId: string | null) => {
    // Tapping the active chip again collapses the form and drops any unsaved
    // edits. Tapping a different chip resets the form to that mode's defaults.
    if (groupId === activeGroupId) {
      setActiveGroupId(undefined);
      setSelectedIds([]);
      setName("");
      setMinCapacity("");
      setMaxCapacity("");
      setNameTouched(false);
      return;
    }
    setActiveGroupId(groupId);
    const target = groupId == null ? null : (existingGroups.find((g) => g.id === groupId) ?? null);
    if (target) {
      setSelectedIds(target.tables.filter((id) => tableIds.includes(id)));
      setName(target.name);
      setMinCapacity(target.min_capacity);
      setMaxCapacity(target.max_capacity);
      setNameTouched(true);
    } else {
      setSelectedIds(tableIds);
      setName(defaultAutoName);
      setMinCapacity(defaultSumMin);
      setMaxCapacity(defaultSumMax);
      setNameTouched(false);
    }
  };

  const selectedTables = selectedIds
    .map((id) => tables.find((t) => t.id === id))
    .filter((t): t is Table => t != null);
  const sumMin = selectedTables.reduce(
    (s, t) => s + (Number.isFinite(t.minCapacity) ? t.minCapacity : 1),
    0,
  );
  const sumMax = selectedTables.reduce(
    (s, t) => s + (Number.isFinite(t.maxCapacity) ? t.maxCapacity : 0),
    0,
  );

  const toggleSelected = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    setSelectedIds(next);
    if (!nameTouched) {
      // Auto-name follows the selection until the user types — keeps the panel
      // useful for the common case while still letting them name custom groups.
      const ordered = candidateTables.filter((t) => next.includes(t.id)).map((t) => t.name);
      setName(ordered.join(" + "));
    }
  };

  const minNum = typeof minCapacity === "number" ? minCapacity : null;
  const maxNum = typeof maxCapacity === "number" ? maxCapacity : null;
  const trimmedName = name.trim();
  const capValid = minNum !== null && maxNum !== null && minNum >= 1 && maxNum >= minNum;
  const enoughTables = selectedIds.length >= MIN_GROUP_TABLES;
  const canSave = trimmedName.length > 0 && capValid && enoughTables && !isSaving;
  const matchesSum = minNum === sumMin && maxNum === sumMax;

  // Hide "+ Nuevo" when every possible 2+-mesa subset of the connected
  // component is already a registered group — there's nothing left to create.
  // For n candidate tables, total subsets of size >= 2 = 2^n - n - 1.
  const totalPossibleSubsets =
    candidateTables.length >= MIN_GROUP_TABLES
      ? 2 ** candidateTables.length - candidateTables.length - 1
      : 0;
  const coveredSubsets = new Set(existingGroups.map((g) => [...g.tables].toSorted().join(",")));
  const canCreateMore = coveredSubsets.size < totalPossibleSubsets;

  return (
    <div
      className="absolute z-[25] bg-white p-4 rounded-2xl w-80 text-sm animate-slide-in overflow-y-auto"
      style={{
        boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
        left: panelPos.left,
        maxHeight: "calc(100dvh - 80px)",
        top: panelPos.top,
      }}
    >
      <div className="flex justify-between items-center mb-4 gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground">
            <Users className="size-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {activeGroupId === undefined ? "Grupo" : activeGroup ? "Editar grupo" : "Nuevo grupo"}
            </h3>
            <p className="text-xs text-text-muted truncate">
              {(formVisible ? selectedTables : candidateTables).map((t) => t.name).join(", ")}
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          onClick={onClose}
        >
          <span className="text-2xl leading-none">&times;</span>
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          {existingGroups.length > 0 && (
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              {canCreateMore ? "Elige uno o crea uno nuevo" : "Elige un grupo"}
            </Label>
          )}
          <div className="flex flex-wrap gap-1.5">
            {existingGroups.map((g) => {
              const isActive = g.id === activeGroupId;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => handleSelectGroup(g.id)}
                  aria-pressed={isActive}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition active:scale-95 ${
                    isActive
                      ? "border-foreground bg-foreground text-white"
                      : "border-black/15 bg-white text-foreground hover:bg-black/5"
                  }`}
                >
                  {g.name}
                </button>
              );
            })}
            {canCreateMore && (
              <button
                type="button"
                onClick={() => handleSelectGroup(null)}
                aria-pressed={activeGroupId === null}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition active:scale-95 ${
                  activeGroupId === null
                    ? "border-foreground bg-foreground text-white"
                    : "border-dashed border-black/25 bg-white text-foreground hover:bg-black/5"
                }`}
              >
                + Nuevo
              </button>
            )}
          </div>
        </div>

        {formVisible && candidateTables.length > MIN_GROUP_TABLES && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Mesas en el grupo
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {candidateTables.map((t) => {
                const isSelected = selectedIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleSelected(t.id)}
                    aria-pressed={isSelected}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition active:scale-95 ${
                      isSelected
                        ? "border-foreground bg-foreground text-white"
                        : "border-black/15 bg-white text-foreground hover:bg-black/5"
                    }`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
            {!enoughTables && (
              <p className="text-[11px] text-red-600">
                Selecciona al menos {MIN_GROUP_TABLES} mesas para crear un grupo.
              </p>
            )}
          </div>
        )}

        {formVisible && (
          <>
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-text-secondary">
              Suma individual:{" "}
              <span className="font-medium text-foreground tabular-nums">
                {sumMin}–{sumMax} personas
              </span>
              {matchesSum && activeGroup === null && (
                <p className="mt-1 text-[11px] text-text-muted">
                  Si dejas estos valores el grupo no se crea (es lo mismo que sumar las mesas).
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                Nombre
              </Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameTouched(true);
                }}
                className="h-11 text-base px-3.5"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Mínimo
                </Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={minCapacity}
                  min={1}
                  max={50}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMinCapacity(raw === "" ? "" : Number(raw));
                  }}
                  className="h-11 text-base px-2 text-center tabular-nums"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Máximo
                </Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={maxCapacity}
                  min={1}
                  max={50}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMaxCapacity(raw === "" ? "" : Number(raw));
                  }}
                  className="h-11 text-base px-2 text-center tabular-nums"
                />
              </div>
            </div>

            {minNum !== null && maxNum !== null && minNum > maxNum && (
              <p className="text-xs text-red-600">El mínimo no puede ser mayor que el máximo.</p>
            )}
          </>
        )}
      </div>

      {formVisible && (
        <div className="mt-5 flex items-stretch gap-2">
          <Button
            className="flex-1 h-11 text-base"
            disabled={!canSave}
            onClick={() => {
              if (!canSave || minNum === null || maxNum === null) return;
              onSave(
                {
                  name: trimmedName,
                  tables: selectedIds,
                  minCapacity: minNum,
                  maxCapacity: maxNum,
                },
                activeGroup?.id ?? null,
              );
            }}
          >
            {activeGroup ? "Actualizar grupo" : "Crear grupo"}
          </Button>
          {activeGroup && (
            <Button
              variant="destructive"
              aria-label="Eliminar grupo"
              title="Eliminar grupo"
              className="size-11 shrink-0 p-0"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={isDeleting}
            >
              <Trash2 className="size-5" />
            </Button>
          )}
        </div>
      )}

      {activeGroup && (
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar el grupo {activeGroup.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                Las mesas seguirán existiendo. El sistema volverá a usar la suma de las capacidades
                individuales para sugerir uniones.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  onDelete(activeGroup.id);
                }}
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

interface ZoneEditPanelProps {
  zone: MapZone;
  panelPos: { left: number; top: number };
  onClose: () => void;
  onSave: (changes: { label?: string; color?: string; border?: string }) => void;
  onDelete: () => void;
}

function ZoneEditPanel({ zone, panelPos, onClose, onSave, onDelete }: ZoneEditPanelProps) {
  const [label, setLabel] = useState(zone.label);
  const [color, setColor] = useState(zone.color);
  const [border, setBorder] = useState(zone.border);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const trimmedLabel = label.trim();
  const isDirty = trimmedLabel !== zone.label || color !== zone.color || border !== zone.border;
  const canSave = isDirty && trimmedLabel.length > 0;

  const kindLabel = zone.isBlock ? "bloque" : "zona";

  const changeRows: { key: string; label: string; from: string; to: string }[] = [];
  if (trimmedLabel !== zone.label) {
    changeRows.push({ key: "label", label: "Nombre", from: zone.label, to: trimmedLabel });
  }
  if (color !== zone.color) {
    changeRows.push({ key: "color", label: "Color fondo", from: zone.color, to: color });
  }
  if (border !== zone.border) {
    changeRows.push({ key: "border", label: "Color borde", from: zone.border, to: border });
  }

  const handleConfirmSave = () => {
    if (!canSave) return;
    const changes: { label?: string; color?: string; border?: string } = {};
    if (trimmedLabel !== zone.label) changes.label = trimmedLabel;
    if (color !== zone.color) changes.color = color;
    if (border !== zone.border) changes.border = border;
    setSaveConfirmOpen(false);
    onSave(changes);
  };

  return (
    <div
      className="absolute z-[25] bg-white p-4 rounded-2xl w-72 text-sm animate-slide-in overflow-y-auto"
      style={{
        boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
        left: panelPos.left,
        maxHeight: "calc(100dvh - 80px)",
        top: panelPos.top,
      }}
    >
      <div className="flex justify-between items-center mb-4 gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground [&>svg]:size-6">
            <ToolbarIcon type={zone.isBlock ? "block" : "zone"} />
          </span>
          <h3 className="text-lg font-semibold text-gray-900 truncate">{label || zone.label}</h3>
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          onClick={onClose}
        >
          <span className="text-2xl leading-none">&times;</span>
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
            Nombre
          </Label>
          <Input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="h-11 text-base px-3.5"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Fondo
            </Label>
            <Input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-11 cursor-pointer p-1"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              Borde
            </Label>
            <Input
              type="color"
              value={border}
              onChange={(e) => setBorder(e.target.value)}
              className="h-11 cursor-pointer p-1"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-stretch gap-2">
        <Button
          className="flex-1 h-11 text-base"
          disabled={!canSave}
          onClick={() => setSaveConfirmOpen(true)}
        >
          Guardar
        </Button>
        <Button
          variant="destructive"
          aria-label="Eliminar"
          title="Eliminar"
          className="size-11 shrink-0 p-0"
          onClick={() => setDeleteConfirmOpen(true)}
        >
          <Trash2 className="size-5" />
        </Button>
      </div>

      <AlertDialog open={saveConfirmOpen} onOpenChange={setSaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar cambios</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a actualizar la {kindLabel} <span className="font-medium">{zone.label}</span>.
              Revisá los cambios antes de continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ul className="mt-2 flex flex-col gap-2 text-sm">
            {changeRows.map((c) => (
              <li key={c.key} className="rounded-md border bg-muted/40 px-3 py-2">
                <div className="font-medium">{c.label}</div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  {c.key === "label" ? (
                    <>
                      <span className="line-through">{c.from}</span>
                      <span>→</span>
                      <span className="text-foreground">{c.to}</span>
                    </>
                  ) : (
                    <>
                      <span
                        className="inline-block size-4 rounded border"
                        style={{ backgroundColor: c.from }}
                        aria-label={c.from}
                      />
                      <span>→</span>
                      <span
                        className="inline-block size-4 rounded border"
                        style={{ backgroundColor: c.to }}
                        aria-label={c.to}
                      />
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Eliminar {zone.isBlock ? "el bloque" : "la zona"} {zone.label}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.{" "}
              {zone.isBlock
                ? "Las mesas que tocaba este bloque quedan donde están."
                : "Las mesas en esta zona quedarán sin zona asignada."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                setDeleteConfirmOpen(false);
                onDelete();
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
